/** @forge-header
 * @id          themeContext
 * @role        Theme context provider | dark/light toggle with system preference fallback
 * @pattern     context-provider
 * @tier        T1
 * @purity      localStorage | dom
 * @units       n/a
 * @upstream    lib/themeManager:initTheme,setTheme,toggleTheme,hasStoredTheme,followSystemTheme
 * @downstream  App.tsx | any component via useTheme()
 * @exports     ThemeProvider:component | useTheme:fn
 * @test        views/Settings/themeSetting.test.tsx
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Theme,
  initTheme,
  hasStoredTheme,
  followSystemTheme,
  setTheme as setThemeStorage,
  toggleTheme as toggleThemeStorage,
} from '@/lib/infra/themeManager';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => initTheme());

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // An explicit choice outranks the OS; without one, the OS is still in
      // charge and this follows it. Following is RECORDED AS NOTHING — the
      // flip used to persist, which meant the first sunset silently pinned the
      // theme and ended the following, with no control anywhere to undo it
      // (#690). What the OS says is not what the trader chose.
      if (hasStoredTheme()) return;
      const newTheme = e.matches ? 'dark' : 'light';
      followSystemTheme(newTheme);
      setThemeState(newTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeStorage(newTheme);
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = toggleThemeStorage();
    setThemeState(newTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
